/**
 * Space item context
 *
 * Shared helpers for serializing Space item context into chat messages
 * and opening matching Space items from Sidekick-driven chat surfaces.
 *
 * Primary responsibilities:
 * - Normalize current Space items into Sidekick context chips
 * - Serialize and parse persisted `[SPACE_ITEM:...]` chat tags
 * - Expose one cross-surface event for opening a Space item
 *
 * Dependencies:
 * - Spaces URL helpers
 * - Shared Spaces item types
 */

import {
  buildSpaceItemHref,
  getSpaceItemFromHref
} from "@/lib/spaces/state"
import type {
  SelectedSpaceItem,
  SpaceLinkedItemType
} from "@/types/spaces"

export type SidekickSpaceItemType =
  | SpaceLinkedItemType
  | "chat"
  | "collection"
  | "event"
  | "file"
  | "space"
  | "whiteboard"

export type SidekickSpaceItemContext = {
  spaceId: string
  type: SidekickSpaceItemType
  id: string
  title: string
  summary?: string | null
}

export type SidekickModalSpaceItemContext = SidekickSpaceItemContext & {
  type: SpaceLinkedItemType
}

export type SidekickOpenSpaceItemEventDetail = SidekickSpaceItemContext & {
  handled?: boolean
}

export type SidekickMention = {
  type: "lead" | "contact" | "company" | "agent" | "space-member"
  id: string
  label: string
}

export const SIDEKICK_OPEN_SPACE_ITEM_EVENT = "sidekick:open-space-item"

const SPACE_ITEM_TAG_REGEX =
  /\[SPACE_ITEM:([^\]:]+):([^\]:]+):([^\]]+)\]([\s\S]*?)\[\/SPACE_ITEM\]\n?/g

const SPACE_ITEM_CARD_TAG_REGEX =
  /\\?\[SPACE_ITEM_CARD\\?\]([\s\S]*?)\\?\[\/SPACE_ITEM_CARD\\?\]\n?/g
const SPACE_ITEM_CARD_CUSTOM_TAG_REGEX =
  /(?:<|&lt;)space-item-card\b([\s\S]*?)(?:&gt;|>)[\s\S]*?(?:&lt;\/space-item-card\s*&gt;|<\/space-item-card\s*>)/gi
const SPACE_ITEM_CARD_DATA_PAYLOAD_REGEX =
  /\bdata-payload\s*=\s*(?:\\"([^\\"]*)\\"|"([^"]*)"|'([^']*)'|&quot;([\s\S]*?)&quot;|&#34;([\s\S]*?)&#34;)/i

const SPACE_ITEM_MARKDOWN_HREF_REGEX =
  /^space-item:([a-z-]+):([^?]+)(?:\?(.+))?$/i

const SPACE_LINKED_ITEM_TYPES = new Set<SpaceLinkedItemType>([
  "task",
  "sheet",
  "note",
  "design",
  "whiteboard",
  "sprint",
  "folder",
  "conversation"
])
const SIDEKICK_SPACE_ITEM_TYPES = new Set<SidekickSpaceItemType>([
  ...SPACE_LINKED_ITEM_TYPES,
  "chat",
  "collection",
  "event",
  "file",
  "whiteboard",
  "space"
])

export const isSidekickModalSpaceItemType = (
  type: SidekickSpaceItemType
): type is SpaceLinkedItemType =>
  SPACE_LINKED_ITEM_TYPES.has(type as SpaceLinkedItemType)

export const isSidekickModalSpaceItem = (
  item: SidekickSpaceItemContext
): item is SidekickModalSpaceItemContext =>
  isSidekickModalSpaceItemType(item.type)

export const markSidekickOpenSpaceItemHandled = (
  detail: SidekickOpenSpaceItemEventDetail | null | undefined
) => {
  if (!detail) {
    return
  }

  detail.handled = true
}

const TYPED_MENTION_REGEX = /@\[([^:\]]+):\s*([^\]]+)\]\(([^:()]+):([^)]+)\)/g
const LINKED_MENTION_REGEX = /\[([^\]]+)\]\(([^:()]+):([^)]+)\)/g
const SIDEKICK_MENTION_TYPES = new Set<SidekickMention["type"]>([
  "lead",
  "contact",
  "company",
  "agent",
  "space-member"
])

const normalizeWhitespace = (value: string | null | undefined) => {
  if (!value) {
    return null
  }

  const normalized = value.replace(/\s+/g, " ").trim()

  return normalized ? normalized : null
}

const decodeMarkerEntities = (value: string) =>
  value
    .replace(/&quot;|&#34;/g, '"')
    .replace(/&apos;|&#39;/g, "'")

const stripWrappingQuotes = (value: string) => {
  let current = value.trim()

  for (let index = 0; index < 2; index += 1) {
    const first = current[0]
    const last = current[current.length - 1]
    const isWrapped =
      current.length >= 2 &&
      ((first === '"' && last === '"') ||
        (first === "'" && last === "'") ||
        (first === "`" && last === "`"))

    if (!isWrapped) {
      break
    }

    current = current.slice(1, -1).trim()
  }

  return current
}

const normalizeSerializedString = (value: string | null | undefined) => {
  const normalized = normalizeWhitespace(value)

  if (!normalized) {
    return null
  }

  const unescaped = decodeMarkerEntities(normalized)
    .replace(/\\"/g, '"')
    .replace(/\\'/g, "'")
  const cleaned = normalizeWhitespace(stripWrappingQuotes(unescaped))

  return cleaned
}

const stripJsonCodeFence = (value: string) => {
  const trimmed = value.trim()
  const fenceMatch = trimmed.match(/^```(?:json)?\s*([\s\S]*?)\s*```$/i)

  return fenceMatch?.[1]?.trim() || trimmed
}

const decodeUriComponentCandidate = (value: string) => {
  try {
    return decodeURIComponent(value)
  } catch {
    return null
  }
}

const getPayloadCandidates = (payload: string) => {
  const candidates: string[] = []
  const pushCandidate = (value: string) => {
    const normalized = value.trim()

    if (normalized && !candidates.includes(normalized)) {
      candidates.push(normalized)
    }
  }

  const withoutFence = stripJsonCodeFence(payload)
  const decoded = decodeMarkerEntities(withoutFence)
  const unwrapped = stripWrappingQuotes(decoded)
  const percentDecoded = decodeUriComponentCandidate(withoutFence)
  const entityPercentDecoded = decodeUriComponentCandidate(decoded)
  const unwrappedPercentDecoded = decodeUriComponentCandidate(unwrapped)

  pushCandidate(payload)
  pushCandidate(withoutFence)
  pushCandidate(decoded)
  pushCandidate(unwrapped)
  if (percentDecoded) pushCandidate(percentDecoded)
  if (entityPercentDecoded) pushCandidate(entityPercentDecoded)
  if (unwrappedPercentDecoded) pushCandidate(unwrappedPercentDecoded)
  pushCandidate(decoded.replace(/\\"/g, '"'))
  pushCandidate(unwrapped.replace(/\\"/g, '"'))

  return candidates
}

const parsePayloadRecord = (payload: string) => {
  const parsed: unknown = JSON.parse(payload)

  if (typeof parsed === "string") {
    return asRecord(JSON.parse(parsed))
  }

  return asRecord(parsed)
}

const truncate = (value: string | null, maxLength: number) => {
  if (!value) {
    return null
  }

  if (value.length <= maxLength) {
    return value
  }

  return `${value.slice(0, maxLength - 1).trimEnd()}…`
}

const normalizeMentionType = (
  value: string | null | undefined
): SidekickMention["type"] | null => {
  const normalized = normalizeWhitespace(value)?.toLowerCase().replace(/_/g, "-")

  if (!normalized) {
    return null
  }

  if (!SIDEKICK_MENTION_TYPES.has(normalized as SidekickMention["type"])) {
    return null
  }

  return normalized as SidekickMention["type"]
}

const appendMention = (
  mentions: SidekickMention[],
  seen: Set<string>,
  mention: SidekickMention
) => {
  const key = `${mention.type}:${mention.id}`

  if (seen.has(key)) {
    return
  }

  seen.add(key)
  mentions.push(mention)
}

const asRecord = (value: unknown) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null
  }

  return value as Record<string, unknown>
}

const getStringField = (
  record: Record<string, unknown>,
  keys: string[]
) => {
  for (const key of keys) {
    const value = record[key]

    if (typeof value !== "string") {
      continue
    }

    const normalized = normalizeSerializedString(value)

    if (normalized) {
      return normalized
    }
  }

  return null
}

const getSpaceIdFromHref = (href: string | null | undefined) => {
  const normalizedHref = normalizeSerializedString(href)

  if (!normalizedHref) {
    return null
  }

  const path = (() => {
    if (normalizedHref.startsWith("/")) {
      return normalizedHref.split(/[?#]/)[0]
    }

    try {
      return new URL(normalizedHref).pathname
    } catch {
      return null
    }
  })()

  const match = path?.replace(/\/+$/, "").match(/^\/spaces\/([^/]+)$/)

  if (!match?.[1]) {
    return null
  }

  try {
    return decodeURIComponent(match[1])
  } catch {
    return match[1]
  }
}

const toSidekickSpaceItemContext = (
  record: Record<string, unknown>
): SidekickSpaceItemContext | null => {
  const href = getStringField(record, ["url", "href"])
  const hrefTarget = getSpaceItemFromHref(href)
  const hrefSpaceId = getSpaceIdFromHref(href)
  const type = (
    getStringField(record, ["type", "itemType", "item_type"]) ||
    hrefTarget?.type ||
    (hrefSpaceId ? "space" : null) ||
    null
  ) as SidekickSpaceItemType | null

  if (!type || !SIDEKICK_SPACE_ITEM_TYPES.has(type)) {
    return null
  }

  const spaceId =
    getStringField(record, ["spaceId", "space_id"]) ||
    (type === "space" ? getStringField(record, ["id", "space"]) : null) ||
    hrefTarget?.spaceId ||
    hrefSpaceId ||
    null
  const id =
    getStringField(record, ["id", "itemId", "item_id"]) ||
    hrefTarget?.id ||
    (type === "space" ? spaceId : null) ||
    null
  const title =
    getStringField(record, ["title", "name"]) ||
    (type === "space" ? "Space" : "Space item")

  if (!spaceId || !id) {
    return null
  }

  return {
    spaceId,
    type,
    id,
    title,
    summary: getStringField(record, ["summary", "description"])
  }
}

export const parseSidekickMentionsFromMessage = (
  message: string
): SidekickMention[] => {
  const mentions: SidekickMention[] = []
  const seen = new Set<string>()
  TYPED_MENTION_REGEX.lastIndex = 0
  LINKED_MENTION_REGEX.lastIndex = 0

  let typedMatch: RegExpExecArray | null
  while ((typedMatch = TYPED_MENTION_REGEX.exec(message)) !== null) {
    const type = normalizeMentionType(typedMatch[3])
    const id = normalizeWhitespace(typedMatch[4])
    const label = normalizeWhitespace(typedMatch[2])

    if (!type || !id || !label) {
      continue
    }

    appendMention(mentions, seen, {
      type,
      id,
      label
    })
  }

  let linkedMatch: RegExpExecArray | null
  while ((linkedMatch = LINKED_MENTION_REGEX.exec(message)) !== null) {
    const rawLabel = normalizeWhitespace(linkedMatch[1])
    const type = normalizeMentionType(linkedMatch[2])
    const id = normalizeWhitespace(linkedMatch[3])

    if (!rawLabel || rawLabel.includes(":") || !type || !id) {
      continue
    }

    appendMention(mentions, seen, {
      type,
      id,
      label: rawLabel.replace(/^@+/, "")
    })
  }

  return mentions
}

const getSpaceItemTitle = (item: NonNullable<SelectedSpaceItem>) => {
  switch (item.type) {
    case "task":
      return normalizeWhitespace(item.data.title) || "Untitled task"
    case "sheet":
      return normalizeWhitespace(item.data.title) || "Untitled sheet"
    case "note":
      return normalizeWhitespace(item.data.title) || "Untitled note"
    case "sprint":
      return normalizeWhitespace(item.data.name) || "Untitled sprint"
    case "folder":
      return normalizeWhitespace(item.data.name) || "Untitled folder"
    case "conversation":
      return normalizeWhitespace(item.data.name) || "Untitled conversation"
  }
}

const getSpaceItemSummary = (item: NonNullable<SelectedSpaceItem>) => {
  switch (item.type) {
    case "task": {
      const parts = [
        normalizeWhitespace(item.data.status),
        normalizeWhitespace(item.data.priority),
        truncate(normalizeWhitespace(item.data.description), 220)
      ].filter((value): value is string => Boolean(value))

      return parts.length > 0 ? parts.join(" • ") : null
    }
    case "sheet":
      return truncate(
        normalizeWhitespace(item.data.description || item.data.title),
        220
      )
    case "note":
      return truncate(normalizeWhitespace(item.data.content), 220)
    case "sprint": {
      const parts = [
        normalizeWhitespace(item.data.status),
        truncate(normalizeWhitespace(item.data.goal), 220)
      ].filter((value): value is string => Boolean(value))

      return parts.length > 0 ? parts.join(" • ") : null
    }
    case "folder":
      return truncate(normalizeWhitespace(item.data.description), 220)
    case "conversation":
      return truncate(normalizeWhitespace(item.data.description), 220)
  }
}

export const buildSidekickSpaceItemContext = (
  item: NonNullable<SelectedSpaceItem>,
  spaceId?: string | null
): SidekickSpaceItemContext | null => {
  const resolvedSpaceId = normalizeWhitespace(item.data.space_id || spaceId || null)

  if (!resolvedSpaceId) {
    return null
  }

  return {
    spaceId: resolvedSpaceId,
    type: item.type,
    id: item.id,
    title: getSpaceItemTitle(item),
    summary: getSpaceItemSummary(item)
  }
}

export const serializeSidekickSpaceItemTag = (
  item: SidekickSpaceItemContext
) => {
  const title = normalizeWhitespace(item.title) || "Space item"

  return `[SPACE_ITEM:${item.spaceId}:${item.type}:${item.id}]${title}[/SPACE_ITEM]`
}

export const serializeSidekickSpaceItemCardTag = (
  item: SidekickSpaceItemContext
) => {
  const payload = {
    spaceId: item.spaceId,
    type: item.type,
    id: item.id,
    title: normalizeWhitespace(item.title) || "Space item",
    ...(item.summary ? { summary: item.summary } : {})
  }

  return `[SPACE_ITEM_CARD]${JSON.stringify(payload)}[/SPACE_ITEM_CARD]`
}

/**
 * Streamdown-safe custom element. URI-encodes the payload so `|` / quotes in
 * summaries cannot break GFM table cells when the card sits inside a table.
 */
export const serializeSidekickSpaceItemCardElement = (
  item: SidekickSpaceItemContext
) => {
  const payload = {
    spaceId: item.spaceId,
    type: item.type,
    id: item.id,
    title: normalizeWhitespace(item.title) || "Space item",
    ...(item.summary ? { summary: item.summary } : {})
  }

  return `<space-item-card data-payload="${encodeURIComponent(
    JSON.stringify(payload)
  )}"></space-item-card>`
}

/**
 * Models sometimes emit broken JSON like `,"",` (empty key) or a trailing bare
 * string (`"title":"X","613-404-7051"}`) instead of `"summary":"…"`. Repair the
 * common cases before giving up.
 */
const salvageSpaceItemCardPayload = (payload: string) => {
  const trimmed = stripJsonCodeFence(decodeMarkerEntities(payload)).trim()
  if (!trimmed) return null

  const repaired = trimmed
    // `,"",` or `, "",` between fields → drop the empty key fragment
    .replace(/,\s*""\s*,/g, ",")
    // trailing `,"")` / `,""]` nonsense
    .replace(/,\s*""\s*([}\]])/g, "$1")
    // missing title key with bare empty string value after id
    .replace(
      /("id"\s*:\s*"[^"]*")\s*,\s*""\s*,\s*("summary")/g,
      '$1,"title":"",$2'
    )
    // trailing bare string value without a key → treat as summary
    // e.g. `"title":"Call Tree Cutting","613-404-7051"}`
    .replace(/,\s*"([^"]*)"\s*(\})/g, ',"summary":"$1"$2')

  return parseSidekickSpaceItemCardPayload(repaired)
}

/** Parse card JSON, then salvage common model malformations. */
export const parseSidekickSpaceItemCardPayloadLenient = (
  payload: string
): SidekickSpaceItemContext | null =>
  parseSidekickSpaceItemCardPayload(payload) ||
  salvageSpaceItemCardPayload(payload)

export const parseSidekickSpaceItemCardPayload = (
  payload: string
): SidekickSpaceItemContext | null => {
  for (const candidate of getPayloadCandidates(payload)) {
    try {
      const record = parsePayloadRecord(candidate)

      if (!record) {
        continue
      }

      return toSidekickSpaceItemContext(record)
    } catch {}
  }

  return null
}

/**
 * Convert bracket SPACE_ITEM_CARD markers to custom elements before markdown
 * so cards render inside table cells without splitting the table apart.
 *
 * Only rewrites cards that sit on a table-row line (pipes around the marker).
 * Standalone cards stay as `[SPACE_ITEM_CARD]` so message-markdown can extract
 * them as COMPONENT_MARKER React nodes (Streamdown custom tags alone are not
 * reliable for block cards outside tables).
 */
export const replaceSpaceItemCardMarkers = (content: string) => {
  if (!content || !content.includes("SPACE_ITEM_CARD")) {
    return content
  }

  return content.replace(SPACE_ITEM_CARD_TAG_REGEX, (match, payload, offset) => {
    const absoluteOffset = typeof offset === "number" ? offset : 0
    if (!isMatchInsideTableRow(content, absoluteOffset, match.length)) {
      return match
    }

    const item = parseSidekickSpaceItemCardPayloadLenient(payload)

    if (!item) {
      // Prefer an empty cell over raw JSON breaking the table layout.
      return ""
    }

    return serializeSidekickSpaceItemCardElement(item)
  })
}

const isMatchInsideTableRow = (
  content: string,
  matchStart: number,
  matchLength: number
) => {
  const lineStart = content.lastIndexOf("\n", matchStart - 1) + 1
  const nextBreak = content.indexOf("\n", matchStart)
  const lineEnd = nextBreak === -1 ? content.length : nextBreak
  const line = content.slice(lineStart, lineEnd)
  const localStart = matchStart - lineStart
  const before = line.slice(0, localStart)
  const after = line.slice(localStart + matchLength)

  return before.includes("|") || after.includes("|")
}

const normalizeContent = (content: string | null | undefined) =>
  typeof content === "string" ? content : ""

export const parseSidekickSpaceItemTags = (content: string | null | undefined) => {
  const items: SidekickSpaceItemContext[] = []
  const sourceContent = normalizeContent(content)

  const contentWithoutTags = sourceContent.replace(
    SPACE_ITEM_TAG_REGEX,
    (_match, spaceId, type, id, title) => {
      const normalizedTitle = normalizeWhitespace(title) || "Space item"

      items.push({
        spaceId,
        type: type as SidekickSpaceItemContext["type"],
        id,
        title: normalizedTitle
      })

      return ""
    }
  )

  return {
    items,
    contentWithoutTags
  }
}

export const parseSidekickSpaceItemCardTags = (content: string | null | undefined) => {
  const items: SidekickSpaceItemContext[] = []
  const sourceContent = normalizeContent(content)
  const appendItem = (payload: string | null | undefined) => {
    const item = payload ? parseSidekickSpaceItemCardPayload(payload) : null

    if (item) {
      items.push(item)
    }
  }

  const contentWithoutMarkerTags = sourceContent.replace(
    SPACE_ITEM_CARD_TAG_REGEX,
    (_match, payload) => {
      appendItem(payload)

      return ""
    }
  )
  const contentWithoutTags = contentWithoutMarkerTags.replace(
    SPACE_ITEM_CARD_CUSTOM_TAG_REGEX,
    (_match, attributes) => {
      const payloadMatch = String(attributes || "").match(
        SPACE_ITEM_CARD_DATA_PAYLOAD_REGEX
      )
      appendItem(
        payloadMatch?.[1] ||
          payloadMatch?.[2] ||
          payloadMatch?.[3] ||
          payloadMatch?.[4] ||
          payloadMatch?.[5]
      )

      return ""
    }
  )

  return {
    items,
    contentWithoutTags
  }
}

export const stripSidekickSpaceItemTags = (content: string | null | undefined) =>
  parseSidekickSpaceItemCardTags(
    parseSidekickSpaceItemTags(content).contentWithoutTags
  ).contentWithoutTags

export const getSidekickSpaceItemHref = (item: SidekickSpaceItemContext) => {
  const spaceId = encodeURIComponent(item.spaceId || item.id)
  const itemId = encodeURIComponent(item.id)

  switch (item.type) {
    case "chat":
      return `/chat/${itemId}`
    case "collection":
      return `/spaces/${spaceId}?tab=files&collection=${itemId}`
    case "event":
      return `/spaces/${spaceId}?tab=calendar&event=${itemId}`
    case "file":
      return `/spaces/${spaceId}?tab=files&file=${itemId}`
    case "space":
      return `/spaces/${spaceId}`
    case "whiteboard":
      return `/spaces/${spaceId}/whiteboards/${itemId}`
    default:
      return buildSpaceItemHref(item.spaceId, {
        type: item.type,
        id: item.id
      })
  }
}

export const buildSidekickSpaceItemMarkdownHref = (
  item: SidekickSpaceItemContext
) =>
  `space-item:${item.type}:${encodeURIComponent(item.id)}?spaceId=${encodeURIComponent(item.spaceId)}`

export const parseSidekickSpaceItemMarkdownHref = (
  href: string,
  title?: string | null
): SidekickSpaceItemContext | null => {
  const match = href.match(SPACE_ITEM_MARKDOWN_HREF_REGEX)

  if (!match) {
    return null
  }

  const type = normalizeWhitespace(match[1]) as SidekickSpaceItemType | null

  if (!type || !SIDEKICK_SPACE_ITEM_TYPES.has(type)) {
    return null
  }

  const id = normalizeWhitespace(decodeURIComponent(match[2]))
  const params = new URLSearchParams(match[3] || "")
  const spaceId = normalizeWhitespace(params.get("spaceId"))
  const normalizedTitle =
    normalizeWhitespace(title) || (type === "space" ? "Space" : "Space item")

  if (!id || !spaceId) {
    return null
  }

  return {
    spaceId,
    type,
    id,
    title: normalizedTitle
  }
}

export const dispatchSidekickOpenSpaceItem = (
  item: SidekickSpaceItemContext
): boolean => {
  if (typeof window === "undefined") {
    return false
  }

  const detail: SidekickOpenSpaceItemEventDetail = {
    ...item,
    handled: false
  }

  window.dispatchEvent(
    new CustomEvent<SidekickOpenSpaceItemEventDetail>(
      SIDEKICK_OPEN_SPACE_ITEM_EVENT,
      {
        detail
      }
    )
  )

  return detail.handled === true
}
